import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  History, TrendingUp, TrendingDown, Target, AlertTriangle, CheckCircle, 
  Clock, DollarSign, BarChart3, Filter, Loader2, RefreshCw, Wallet, 
  Activity, ArrowUpRight, ArrowDownRight, Calendar
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { API, getAuthHeaders } from '../utils/api';
import TradingPanel from '../components/TradingPanel';

export default function TradeHistory() {
  const [trades, setTrades] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [historyRes, accountRes] = await Promise.all([
        axios.get(`${API}/trading/history?limit=100`, { 
          withCredentials: true, 
          headers: getAuthHeaders(),
          timeout: 10000 
        }),
        axios.get(`${API}/trading/account`, { 
          withCredentials: true, 
          headers: getAuthHeaders(),
          timeout: 10000 
        })
      ]);
      
      setTrades(historyRes.data.trades || []);
      setAccount(accountRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load trade history');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from trades
  const stats = {
    total_trades: trades.length,
    winning_trades: trades.filter(t => t.realized_pnl > 0).length,
    losing_trades: trades.filter(t => t.realized_pnl < 0).length,
    total_pnl: trades.reduce((sum, t) => sum + (t.realized_pnl || 0), 0),
    win_rate: trades.length > 0 
      ? (trades.filter(t => t.realized_pnl > 0).length / trades.length * 100).toFixed(1)
      : 0,
    best_trade: Math.max(...trades.map(t => t.realized_pnl || 0), 0),
    worst_trade: Math.min(...trades.map(t => t.realized_pnl || 0), 0)
  };

  const filteredTrades = trades.filter(trade => {
    if (filter === 'all') return true;
    if (filter === 'win') return trade.realized_pnl > 0;
    if (filter === 'loss') return trade.realized_pnl < 0;
    return true;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope']">Trade History</h1>
            <p className="text-slate-400">Track your trading performance</p>
          </div>
          <Button 
            onClick={fetchData}
            variant="outline"
            className="border-zinc-700"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Account Overview */}
        {account && (
          <Card className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border-zinc-700 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-gradient-to-br from-blue-500/30 to-blue-600/20 rounded-xl border border-blue-500/30">
                <Wallet className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Trading Account</h3>
                <p className="text-xs text-slate-400">Account: {account.account_id}</p>
              </div>
              <Badge variant="outline" className="ml-auto bg-amber-500/20 border-amber-500/50 text-amber-400">
                PAPER TRADING
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <p className="text-slate-500 text-xs mb-1">Balance</p>
                <p className="text-2xl font-bold text-white font-mono">${account.balance?.toFixed(2)}</p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <p className="text-slate-500 text-xs mb-1">Total P&L</p>
                <p className={`text-2xl font-bold font-mono ${account.total_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {account.total_profit >= 0 ? '+' : ''}${account.total_profit?.toFixed(2)}
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <p className="text-slate-500 text-xs mb-1">Win Rate</p>
                <p className="text-2xl font-bold text-white font-mono">
                  {account.total_trades > 0 
                    ? ((account.winning_trades / account.total_trades) * 100).toFixed(1)
                    : '0.0'}%
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <p className="text-slate-500 text-xs mb-1">Total Trades</p>
                <p className="text-2xl font-bold text-white font-mono">{account.total_trades || 0}</p>
                <p className="text-[10px] text-slate-500">{account.winning_trades}W / {account.losing_trades}L</p>
              </div>
            </div>
          </Card>
        )}

        <Tabs defaultValue="history" className="w-full">
          <TabsList className="bg-zinc-800/50 border border-zinc-700">
            <TabsTrigger value="history" className="data-[state=active]:bg-zinc-700">
              <History className="w-4 h-4 mr-2" />
              Trade History
            </TabsTrigger>
            <TabsTrigger value="trade" className="data-[state=active]:bg-zinc-700">
              <Activity className="w-4 h-4 mr-2" />
              Trade Now
            </TabsTrigger>
          </TabsList>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-zinc-900 border-zinc-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-slate-500">Total Trades</span>
                </div>
                <p className="text-2xl font-bold font-mono">{stats.total_trades}</p>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs text-slate-500">Win Rate</span>
                </div>
                <p className="text-2xl font-bold font-mono text-emerald-400">{stats.win_rate}%</p>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs text-slate-500">Best Trade</span>
                </div>
                <p className="text-2xl font-bold font-mono text-emerald-400">+${stats.best_trade.toFixed(2)}</p>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                  <span className="text-xs text-slate-500">Worst Trade</span>
                </div>
                <p className="text-2xl font-bold font-mono text-red-400">${stats.worst_trade.toFixed(2)}</p>
              </Card>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-slate-500" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Filter trades" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Trades</SelectItem>
                  <SelectItem value="win">Winning</SelectItem>
                  <SelectItem value="loss">Losing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Trades List */}
            <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
                </div>
              ) : filteredTrades.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-semibold">No trades yet</p>
                  <p className="text-sm">Start trading to see your history here</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {filteredTrades.map((trade) => (
                    <div 
                      key={trade.position_id}
                      className="p-4 hover:bg-zinc-800/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            trade.realized_pnl > 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'
                          }`}>
                            {trade.realized_pnl > 0 ? (
                              <TrendingUp className="w-5 h-5 text-emerald-400" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold">{trade.market}</span>
                              <Badge className={trade.direction === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                                {trade.direction?.toUpperCase()}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                              <span>{trade.lot_size} lots</span>
                              <span>Entry: {trade.entry_price?.toFixed(2)}</span>
                              <span>Exit: {trade.close_price?.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className={`text-lg font-bold font-mono ${trade.realized_pnl > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {trade.realized_pnl > 0 ? '+' : ''}${trade.realized_pnl?.toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500">{formatDate(trade.closed_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Trade Now Tab */}
          <TabsContent value="trade" className="mt-6">
            <TradingPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
